import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { ipoService } from '../../api/ipoService';

// Safety cap on page requests if the reported total ever keeps growing
const MAX_PAGES = 10;

/**
 * Async thunk to fetch the full SME IPO list
 * GET /api/core/ipos/sme -> { data: [...], total, page, page_size, refreshed_at, error }
 * Follows the pagination until every reported item is loaded.
 *
 * options.maxAgeMs: skip the request when data this recent is already loaded
 */
export const fetchSmeIpos = createAsyncThunk(
  'smeIpos/fetchSmeIpos',
  async (_options, { rejectWithValue }) => {
    try {
      const first = await ipoService.getSmeIpos();
      if (first?.success === false) {
        return rejectWithValue(first.error || 'Failed to fetch SME IPOs');
      }
      const items = Array.isArray(first?.data) ? [...first.data] : [];
      const total = typeof first?.total === 'number' ? first.total : items.length;

      for (let page = 2; items.length < total && page <= MAX_PAGES; page += 1) {
        const next = await ipoService.getSmeIpos({ page });
        const batch = Array.isArray(next?.data) ? next.data : [];
        if (!batch.length) break;
        items.push(...batch);
      }

      return { items, total, refreshedAt: first?.refreshed_at || null, note: first?.error || null };
    } catch (error) {
      return rejectWithValue(error.message || 'Failed to fetch SME IPOs');
    }
  },
  {
    condition: ({ maxAgeMs = 0 } = {}, { getState }) => {
      const { status, fetchedAt } = getState().smeIpos;
      // Skip duplicate requests while one is in flight (e.g. StrictMode double effects)
      if (status === 'loading') return false;
      if (maxAgeMs && status === 'succeeded' && fetchedAt && Date.now() - fetchedAt < maxAgeMs) {
        return false;
      }
      return true;
    },
  }
);

const initialState = {
  // Raw items from the API; the page merges them with utils/smeIpos.js buildSmeIpoRows
  items: [],
  total: 0,
  refreshedAt: null,
  // The API's soft message when the list is empty or partial
  note: null,
  fetchedAt: null,
  status: 'idle', // 'idle' | 'loading' | 'succeeded' | 'failed'
  error: null,
};

export const smeIposSlice = createSlice({
  name: 'smeIpos',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchSmeIpos.pending, (state) => {
        state.status = 'loading';
        state.error = null;
      })
      .addCase(fetchSmeIpos.fulfilled, (state, action) => {
        const { items, total, refreshedAt, note } = action.payload;
        state.status = 'succeeded';
        state.items = items;
        state.total = total;
        state.refreshedAt = refreshedAt;
        state.note = note;
        state.fetchedAt = Date.now();
      })
      .addCase(fetchSmeIpos.rejected, (state, action) => {
        // Keep the previously loaded list so a failed refresh doesn't blank the page
        state.status = 'failed';
        state.error = action.payload || 'Failed to load SME IPOs';
      });
  },
});

export default smeIposSlice.reducer;
